import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Minus } from "lucide-react";

// Popover to add or subtract stock by pallets / cases / units using the variant's packaging.
export function StockPopover({ t, mode, onApply, testId }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState("1");
  const isSub = mode === "subtract";
  const Icon = isSub ? Minus : Plus;
  const isWeight = t.packaging_mode === "weight" && (t.weight_per_unit || 0) > 0;
  const wUnit = t.weight_unit || "lbs";
  const pkgLabel = t.package_type ? t.package_type.charAt(0).toUpperCase() + t.package_type.slice(1) + "s" : "Packages";
  const upc = t.units_per_case || null;
  const cpp = t.cases_per_pallet || null;
  const upp = t.units_per_pallet || (upc && cpp ? upc * cpp : null);
  const opts = isWeight
    ? [
        { v: "weight", label: wUnit, per: 1 },
        { v: "packages", label: pkgLabel, per: t.weight_per_unit },
        ...(t.packages_per_pallet ? [{ v: "pallets", label: "Pallets", per: t.weight_per_unit * t.packages_per_pallet }] : []),
      ]
    : [
        { v: "units", label: "Units", per: 1 },
        ...(upc ? [{ v: "cases", label: "Cases", per: upc }] : []),
        ...(upp ? [{ v: "pallets", label: "Pallets", per: upp }] : []),
      ];
  const [unit, setUnit] = useState(opts[isWeight ? 1 : 0].v);
  const per = (opts.find((o) => o.v === unit) || opts[0]).per || 1;
  const delta = (parseFloat(qty) || 0) * per;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={`rounded-sm border border-slate-200 p-1 hover:bg-slate-100 ${isSub ? "text-slate-500 hover:text-red-600" : "text-[#002FA7]"}`} title={isSub ? "Subtract stock" : "Add stock"} data-testid={testId}><Icon className="h-3.5 w-3.5" /></button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 rounded-sm p-3" data-testid={`${testId}-panel`}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{isSub ? "Subtract stock" : "Add stock"}</p>
        <div className="flex gap-2">
          <Input type="text" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9.]/g, ""))} className="h-8 rounded-sm text-right tabular-nums" data-testid={`${testId}-qty`} />
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger className="h-8 w-28 rounded-sm" data-testid={`${testId}-unit`}><SelectValue /></SelectTrigger>
            <SelectContent>{opts.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <p className="mt-2 text-[11px] text-slate-500">
          {isSub ? "Removes" : "Adds"} {isWeight
            ? `${(Math.round(delta * 100) / 100).toLocaleString()} ${wUnit} (≈ ${(Math.round((delta / t.weight_per_unit) * 100) / 100).toLocaleString()} ${pkgLabel.toLowerCase()})`
            : `${Math.round(delta).toLocaleString()} units`} {isSub ? "from" : "to"} stock
        </p>
        {!isWeight && !upc && !upp && <p className="mt-1 text-[10px] text-slate-400">Set packaging on this variant to use cases or pallets.</p>}
        <Button size="sm" className={`mt-2 h-8 w-full rounded-sm text-white ${isSub ? "bg-red-600 hover:bg-red-700" : "bg-[#002FA7] hover:bg-[#002080]"}`} disabled={delta <= 0} onClick={() => { onApply(isSub ? -delta : delta); setOpen(false); setQty("1"); }} data-testid={`${testId}-apply`}>{isSub ? "Subtract from stock" : "Add to stock"}</Button>
      </PopoverContent>
    </Popover>
  );
}
